import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from './ui/FadeIn';
import { X, ChevronLeft, ChevronRight, Camera, Pause, Play } from 'lucide-react';

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  wedding: string;
}

// Wedding gallery collections with actual photos
const weddingCollections = [
  {
    id: 'wedding-01',
    title: 'Elegant Moments',
    location: 'Mumbai',
    images: [
      'IMG_6991.webp', 'IMG_6996.webp', 'IMG_7003.webp', 'IMG_7005.webp',
      'IMG_7076.webp', 'IMG_7078.webp', 'IMG_7083.webp', 'IMG_7092.webp',
      'IMG_7094.webp', 'IMG_7098.webp', 'IMG_7100.webp', 'IMG_7101.webp',
      'IMG_7102.webp', 'IMG_7113.webp'
    ]
  },
  {
    id: 'wedding-02',
    title: 'Royal Celebration',
    location: 'Jaipur',
    images: [
      '1F2A3862.webp', '1F2A3885.webp', '1F2A3889.webp', '1F2A3895.webp',
      '1F2A3896.webp', '1F2A3902.webp', '1F2A3906.webp', '1F2A3908.webp',
      '1F2A3919.webp', '1F2A3951.webp', '1F2A3979.webp', '1F2A3983.webp',
      '1F2A3984.webp', '1F2A3986.webp', '1F2A3988.webp', '1F2A3993.webp',
      '1F2A4001.webp', '1F2A4010.webp', '1F2A4042.webp', '1F2A4052.webp',
      '1F2A4056.webp', '1F2A4070.webp'
    ]
  },
  {
    id: 'wedding-03',
    title: 'Timeless Love',
    location: 'Udaipur',
    images: [
      '1F2A0059.webp', '1F2A0083.webp', '1F2A0336.webp', '1F2A0462.webp',
      '1F2A0504.webp', '1F2A0514.webp', '1F2A0518.webp', '1F2A0520.webp',
      '1F2A0532.webp', '1F2A0533.webp', '1F2A0570.webp', '1F2A0571.webp',
      '1F2A0852.webp', '1F2A0864.webp', '1F2A1062.webp', '1F2A1104.webp',
      '1F2A1298.webp', '1F2A1309.webp'
    ]
  },
  {
    id: 'wedding-04',
    title: 'Grand Heritage',
    location: 'Jodhpur',
    images: [
      '1F2A0134.webp', '1F2A0150.webp', '1F2A0201.webp', '1F2A0793.webp',
      '1F2A1062.webp', '1F2A1377.webp', '1F2A1387.webp', '1F2A1398.webp',
      '1F2A1424.webp', '1F2A1441.webp', '1F2A1470.webp', '1F2A1484_1.webp',
      '1F2A1521.webp', '1F2A1604.webp', '1F2A1879.webp', '1F2A1920.webp',
      '1F2A1954.webp', '1F2A1971.webp', '1F2A1980.webp', '1F2A2185.webp',
      '1F2A2186.webp', '1F2A2254.webp', '1F2A2283.webp', '1F2A2287.webp',
      '1F2A2296.webp', '1F2A2322.webp', '1F2A2341.webp', '1F2A2379.webp',
      '1F2A2451.webp', '1F2A2626.webp', '1F2A2693.webp', '1F2A2721.webp',
      '1F2A2732.webp', '1F2A2744.webp', '1F2A2966.webp', '1F2A8171.webp',
      '1F2A8211.webp', '1F2A8236.webp', '1F2A8265_1.webp', '1F2A8287.webp',
      '1F2A8534.webp'
    ]
  }
];

// Build flat gallery images for display - curated selection
const allImages: GalleryImage[] = [
  // Wedding 01 - Best picks
  { id: 1, src: '/gallery/wedding-01/IMG_6991.webp', alt: 'Wedding moment', wedding: 'Elegant Moments' },
  { id: 2, src: '/gallery/wedding-01/IMG_7076.webp', alt: 'Wedding celebration', wedding: 'Elegant Moments' },
  { id: 3, src: '/gallery/wedding-01/IMG_7094.webp', alt: 'Wedding portrait', wedding: 'Elegant Moments' },
  { id: 4, src: '/gallery/wedding-01/IMG_7100.webp', alt: 'Wedding ceremony', wedding: 'Elegant Moments' },
  
  // Wedding 02 - Best picks
  { id: 5, src: '/gallery/wedding-02/1F2A3862.webp', alt: 'Royal wedding', wedding: 'Royal Celebration' },
  { id: 6, src: '/gallery/wedding-02/1F2A3919.webp', alt: 'Wedding ritual', wedding: 'Royal Celebration' },
  { id: 7, src: '/gallery/wedding-02/1F2A3951.webp', alt: 'Wedding moment', wedding: 'Royal Celebration' },
  { id: 8, src: '/gallery/wedding-02/1F2A4010.webp', alt: 'Wedding celebration', wedding: 'Royal Celebration' },
  { id: 9, src: '/gallery/wedding-02/1F2A4056.webp', alt: 'Wedding portrait', wedding: 'Royal Celebration' },
  
  // Wedding 03 - Best picks
  { id: 10, src: '/gallery/wedding-03/1F2A0059.webp', alt: 'Palace wedding', wedding: 'Timeless Love' },
  { id: 11, src: '/gallery/wedding-03/1F2A0504.webp', alt: 'Wedding ceremony', wedding: 'Timeless Love' },
  { id: 12, src: '/gallery/wedding-03/1F2A0570.webp', alt: 'Wedding moment', wedding: 'Timeless Love' },
  { id: 13, src: '/gallery/wedding-03/1F2A1062.webp', alt: 'Wedding celebration', wedding: 'Timeless Love' },
  
  // Wedding 04 - Best picks
  { id: 14, src: '/gallery/wedding-04/1F2A0201.webp', alt: 'Heritage wedding', wedding: 'Grand Heritage' },
  { id: 15, src: '/gallery/wedding-04/1F2A1398.webp', alt: 'Wedding ritual', wedding: 'Grand Heritage' },
  { id: 16, src: '/gallery/wedding-04/1F2A1604.webp', alt: 'Wedding moment', wedding: 'Grand Heritage' },
  { id: 17, src: '/gallery/wedding-04/1F2A1920.webp', alt: 'Wedding celebration', wedding: 'Grand Heritage' },
  { id: 18, src: '/gallery/wedding-04/1F2A2296.webp', alt: 'Wedding portrait', wedding: 'Grand Heritage' },
  { id: 19, src: '/gallery/wedding-04/1F2A2626.webp', alt: 'Wedding ceremony', wedding: 'Grand Heritage' },
  { id: 20, src: '/gallery/wedding-04/1F2A8287.webp', alt: 'Wedding finale', wedding: 'Grand Heritage' },
];

export const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setSliderIndex(prev => (prev + 1) % allImages.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const openLightbox = useCallback((image: GalleryImage, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  }, []);

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? allImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === allImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(allImages[newIndex]);
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, currentIndex, closeLightbox, goToPrevious, goToNext]);

  // Get 5 visible images for the creative stack
  const getVisibleImages = () => {
    const visible = [];
    for (let i = 0; i < 5; i++) {
      const index = (sliderIndex + i) % allImages.length;
      visible.push({ ...allImages[index], position: i });
    }
    return visible;
  };

  const visibleImages = getVisibleImages();

  return (
    <>
      <section id="gallery" className="py-24 md:py-40 bg-stone-100 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <FadeIn className="mb-20 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Camera size={20} className="text-[#D4AF37]" strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400">Photography</p>
            </div>
            <h2 className="text-4xl md:text-6xl font-serif text-stone-900 mb-4">Captured Moments</h2>
            <p className="text-stone-500 font-light max-w-lg mx-auto">
              Beyond cinematography, we capture the stillness of your most precious moments.
            </p>
            <div className="w-12 h-[1px] bg-[#D4AF37] mx-auto mt-8"></div>
          </FadeIn>

          {/* Creative Stacked Photo Slider */}
          <div className="relative h-[500px] md:h-[700px] flex items-center justify-center mb-16">
            {/* Stacked Images */}
            <div ref={sliderRef} className="relative w-full max-w-4xl h-full">
              {visibleImages.map((image, idx) => {
                // Creative positioning based on index
                const positions = [
                  { left: '50%', top: '50%', rotate: -3, scale: 1, zIndex: 50, opacity: 1 }, // Center (main)
                  { left: '75%', top: '45%', rotate: 5, scale: 0.7, zIndex: 40, opacity: 0.9 }, // Right
                  { left: '25%', top: '55%', rotate: -8, scale: 0.65, zIndex: 30, opacity: 0.85 }, // Left
                  { left: '85%', top: '60%', rotate: 12, scale: 0.5, zIndex: 20, opacity: 0.7 }, // Far right
                  { left: '15%', top: '40%', rotate: -12, scale: 0.5, zIndex: 10, opacity: 0.7 }, // Far left
                ];
                
                const pos = positions[idx];
                
                return (
                  <div
                    key={image.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-700 ease-out shadow-2xl hover:shadow-3xl bg-white p-2"
                    style={{
                      left: pos.left,
                      top: pos.top,
                      transform: `translate(-50%, -50%) rotate(${pos.rotate}deg) scale(${pos.scale})`,
                      zIndex: pos.zIndex,
                      opacity: pos.opacity,
                    }}
                    onClick={() => openLightbox(image, allImages.findIndex(img => img.id === image.id))}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-[280px] md:w-[400px] h-[350px] md:h-[500px] object-cover"
                        loading="lazy"
                      />
                      {idx === 0 && (
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent">
                          <p className="text-white text-sm font-serif">{image.wedding}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Slider Controls */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 z-60">
              <button
                onClick={() => setSliderIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                className="bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors"
              >
                <ChevronLeft size={20} className="text-stone-700" />
              </button>
              
              <button
                onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                className="bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors"
              >
                {isAutoPlaying ? (
                  <Pause size={16} className="text-stone-700" />
                ) : (
                  <Play size={16} className="text-stone-700 ml-0.5" />
                )}
              </button>
              
              <button
                onClick={() => setSliderIndex(prev => (prev + 1) % allImages.length)}
                className="bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition-colors"
              >
                <ChevronRight size={20} className="text-stone-700" />
              </button>
            </div>
            
            {/* Progress Indicator */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5 z-60">
              {allImages.slice(0, 10).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setSliderIndex(idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    sliderIndex % 10 === idx ? 'bg-[#D4AF37] w-4' : 'bg-stone-300 hover:bg-stone-400'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Wedding Collections Grid */}
          <FadeIn className="mt-24">
            <h3 className="text-center text-sm tracking-[0.2em] uppercase text-stone-400 mb-12">Wedding Collections</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {weddingCollections.map((collection, idx) => (
                <FadeIn key={collection.id} delay={idx * 100}>
                  <div className="group cursor-pointer">
                    <div className="relative overflow-hidden aspect-square shadow-lg">
                      <img
                        src={`/gallery/${collection.id}/${collection.images[0]}`}
                        alt={collection.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-white/60">{collection.location}</p>
                        <p className="text-white font-serif text-lg">{collection.title}</p>
                        <p className="text-white/50 text-xs mt-1">{collection.images.length} photos</p>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
            
            {/* View Full Gallery Button */}
            <div className="text-center mt-12">
              <Link 
                to="/gallery" 
                className="inline-block text-xs uppercase tracking-[0.2em] border border-stone-800 px-8 py-4 hover:bg-stone-800 hover:text-stone-50 transition-all duration-300"
              >
                View Full Gallery
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-stone-950/98 backdrop-blur-lg flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-[110]"
            onClick={closeLightbox}
          >
            <X size={32} strokeWidth={1} />
          </button>

          {/* Previous Button */}
          <button
            className="absolute left-4 md:left-8 text-white/30 hover:text-white transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
          >
            <ChevronLeft size={48} strokeWidth={1} />
          </button>

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain shadow-2xl"
            />
            <div className="absolute -bottom-16 left-0 right-0 text-center">
              <p className="text-white/80 font-serif text-xl">{selectedImage.wedding}</p>
              <p className="text-white/40 text-sm mt-2">{currentIndex + 1} / {allImages.length}</p>
            </div>
          </div>

          {/* Next Button */}
          <button
            className="absolute right-4 md:right-8 text-white/30 hover:text-white transition-colors p-2"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
          >
            <ChevronRight size={48} strokeWidth={1} />
          </button>
        </div>
      )}
    </>
  );
};
